import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { Product } from '@/types';
import Button from '@/components/ui/Button';
import { HeartIcon, ShoppingCartIcon, EyeIcon } from '@heroicons/react/24/outline';
import { wishlistApi } from '@/services/api';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { addToCart } from '@/store/slices/cartSlice';

const WishlistPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [wishlist, setWishlist] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await wishlistApi.getMyWishlist();
        
        // Handle empty wishlist gracefully
        if (!response.data || !response.data.items || response.data.items.length === 0) {
          setWishlist([]);
          setIsLoading(false);
          return;
        }
        
        // Transform API response - extract products from wishlist items
        const products: Product[] = response.data.items
          .filter((item: any) => item.product) // Filter out items without products
          .map((item: any) => ({
            ...item.product,
            wishlist_id: item.id // Store wishlist ID for removal
          }));
        setWishlist(products);
      } catch (err: any) {
        console.error('Error fetching wishlist:', err);
        
        // Handle connection errors, table not found, or empty responses as empty wishlist
        const isConnectionError = err.code === 'ERR_NETWORK' || 
                                  err.code === 'ERR_CONNECTION_REFUSED' ||
                                  err.message?.includes('Network Error');
        const isNotFound = err.response?.status === 404;
        const isTableError = err.response?.data?.detail?.includes('table') || 
                           err.response?.data?.detail?.includes('does not exist');
        
        if (isConnectionError || isNotFound || isTableError) {
          // Treat as empty wishlist - not an error state
          setWishlist([]);
          setError(null);
        } else {
          // Only show error for actual API failures
          const errorMessage = err.response?.data?.detail || err.message || 'Failed to load wishlist';
          setError(errorMessage);
          toast.error('Failed to load wishlist');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchWishlist();
  }, [user]);

  const handleRemoveFromWishlist = async (productId: number) => {
    try {
      await wishlistApi.removeFromWishlist(productId);
      setWishlist(prev => prev.filter(product => product.id !== productId));
      toast.success('Removed from wishlist');
    } catch (err: any) {
      console.error('Error removing from wishlist:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove from wishlist');
    }
  };

  const handleAddToCart = async (product: Product) => {
    try {
      await dispatch(addToCart({ product_id: product.id, quantity: 1 }) as any).unwrap();
      toast.success('Added to cart');
    } catch (err: any) {
      console.error('Error adding to cart:', err);
      toast.error(err.response?.data?.detail || 'Failed to add to cart');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  // Only show error if there's an actual error and wishlist is empty
  // If wishlist has items, don't show error (might be a partial load issue)
  if (error && wishlist.length === 0 && !isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => {
          setError(null);
          window.location.reload();
        }}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Wishlist</h1>
        <p className="mt-2 text-gray-600">Products you've saved for later</p>
      </div>

      {wishlist.length === 0 ? (
        <div className="text-center py-12">
          <HeartIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your wishlist is empty</h3>
          <p className="text-gray-600 mb-6">Start adding products you love to your wishlist</p>
          <Button onClick={() => window.location.href = '/products'}>
            Browse Products
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {wishlist.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={getPrimaryImage(product.images) || '/api/placeholder/300/300'}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-2xl font-bold text-primary-600">
                      {formatCurrency(product.price)}
                    </span>
                    {product.original_price && product.original_price > product.price && (
                      <span className="text-sm text-gray-500 line-through ml-2">
                        {formatCurrency(product.original_price)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {product.condition}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `/products/${product.id}`}
                    className="flex-1"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAddToCart(product)}
                    className="flex-1"
                  >
                    <ShoppingCartIcon className="h-4 w-4 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveFromWishlist(product.id)}
                    className="px-3"
                  >
                    <HeartIcon className="h-4 w-4 text-red-500 fill-current" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WishlistPage;